import { useCallback, useEffect } from 'react'

import { Add } from '@mui/icons-material'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useGetList, useTranslate } from 'react-admin'
import { useImmer } from 'use-immer'
import { v4 as uuidv4 } from 'uuid'
import {
  ArtworkApiDTO,
  BeneficiaryApiDTO,
  CompilationApiDTO,
} from '../../codegen/generated/Api.schemas'
import { CompilationStructure } from '../../types/Compilations'
import { CompilationItem } from './CompilationItem'

type CompilationItemsListProps = {
  compilation?: CompilationApiDTO
  onUpdate: (linkedArtworkIds: string[]) => void
}

export const CompilationItemsList = ({ compilation, onUpdate }: CompilationItemsListProps) => {
  const translate = useTranslate()
  const [compilationsItems, setCompilationsItems] = useImmer<CompilationStructure[]>([
    {
      id: uuidv4(),
      beneficiary: undefined,
      product: undefined,
      artwork: undefined,
    },
  ])

  const beneficiariesQuery = useGetList<BeneficiaryApiDTO>('beneficiaries', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })

  const onAddLinkedArtwork = useCallback(
    () =>
      setCompilationsItems(_compilations => {
        _compilations.push({
          id: uuidv4(),
          beneficiary: undefined,
          product: undefined,
          artwork: undefined,
        })
      }),
    [setCompilationsItems],
  )

  useEffect(() => {
    onUpdate(
      compilationsItems.filter(c => typeof c.artwork !== 'undefined').map(c => c.artwork?.id ?? ''),
    )
  }, [compilationsItems])

  useEffect(() => {
    if (compilation?.artworks && (compilation?.artworks?.length ?? 0) > 0) {
      setCompilationsItems(
        (compilation.artworks ?? [])?.map(artwork => ({
          id: uuidv4(),
          beneficiary: artwork.product?.beneficiary,
          product: artwork.product,
          artwork: artwork as unknown as ArtworkApiDTO,
        })),
      )
    }
  }, [compilation?.artworks, setCompilationsItems])

  return (
    <Box width="100%">
      <Typography fontWeight="bold" marginBottom={1}>
        {translate('pages.compilations.linkedArtworks')}
      </Typography>
      <Stack gap={3}>
        {compilationsItems.map((c, index) => (
          <CompilationItem
            key={c.id}
            compilationStructure={c}
            beneficiaries={beneficiariesQuery.data ?? []}
            percent={100 / compilationsItems.length}
            isFirst={index === 0}
            onUpdate={compilation => {
              setCompilationsItems(_compilations => {
                _compilations[index] = compilation
                return _compilations
              })
            }}
            onDelete={compilationId => {
              setCompilationsItems(_compilations =>
                _compilations.filter(c => c.id !== compilationId),
              )
            }}
          />
        ))}
      </Stack>
      <Box display="flex" justifyContent="center" marginTop={2}>
        <Button variant="text" size="small" startIcon={<Add />} onClick={onAddLinkedArtwork}>
          {translate('pages.compilations.addLinkedArtwork')}
        </Button>
      </Box>
    </Box>
  )
}
